import { AbsoluteFill } from "remotion";
import { CompositionDto } from "@repo/api/editor";
import { getElementsByScene } from "@repo/api/utils/composition";
import ElementOutline from "./ElementOutline";
import { useMemo } from "react";
import SelectionOutline from "./SelectionOutline";
import SelectionMarquee from "./SelectionMarquee";
import { useGuidelines, useSelectionBoundingBox } from "./element-hooks";
import Guideline from "./Guideline";
import { useElementMotionStore } from "@/lib/stores/element-motion-store/element-motion-store";

type Props = {
  composition: CompositionDto;
};

export const ELEMENT_CONTROLS_ROOT_ID = "element-controls-root";

export default function ElementControlsLayer({ composition }: Props) {
  const elements = useMemo(
    () => [
      ...getElementsByScene(composition, "startScene"),
      ...getElementsByScene(composition, "mainScene"),
      ...getElementsByScene(composition, "endScene"),
    ],
    [composition],
  );

  const pointerState = useElementMotionStore(
    (state) => state.reactivePointerState,
  );
  const resizeHandleType = pointerState?.resizeHandleType ?? null;
  const isMoving =
    pointerState?.type === "POINTER_MOVE_SELECTION" ||
    pointerState?.type === "ARROW_MOVE_SELECTION";
  const isResizing = pointerState?.type === "POINTER_MOVE_RESIZE";

  const selectionBoundingBox = useSelectionBoundingBox();
  const guidelines = useGuidelines(selectionBoundingBox);

  return (
    <AbsoluteFill id={ELEMENT_CONTROLS_ROOT_ID}>
      {elements.map((element) => {
        return (
          <ElementOutline
            key={`element-outline-${element.externalId}`}
            element={element}
            selectionBoundingBox={selectionBoundingBox}
          />
        );
      })}

      <SelectionOutline
        selectionBoundingBox={selectionBoundingBox}
        isMoving={isMoving}
        isResizing={isResizing}
        resizeHandleType={resizeHandleType}
      />
      <SelectionMarquee />
      {guidelines.map((guideline) => {
        const key = `${guideline.type}-${"x" in guideline ? guideline.x : guideline.y}`;

        return (
          <Guideline
            key={key}
            guideline={guideline}
            selectionBoundingBox={selectionBoundingBox}
          />
        );
      })}
    </AbsoluteFill>
  );
}
